// Canonical URL contract for the Global Produce Map (/map).
// Centralized here so every reader/writer parses & serializes the same way.
// Party URL helpers live in mapPartyParams.js (useMapPartyParams owns that path).
import { STATUS_OPTIONS } from '../pages/components/home/WeatherMapControls';

export const LAYER_MODES = ['all', 'shipments', 'weather'];
export const PANEL_VALUES = ['arrivals', 'arrivals3', 'arrivals10', 'alerts'];
export const STATUS_VALUES = STATUS_OPTIONS.map((s) => s.id);

const csvToArray = (raw) =>
  raw ? raw.split(',').map((x) => x.trim()).filter(Boolean) : [];

const safe = (value, allowed, fallback) =>
  allowed.includes(value) ? value : fallback;

/**
 * Parse the `status` URL param into a validated array of status ids.
 * Unknown values are dropped. Used by both readers and the filter strip
 * so parsing logic stays in one place.
 */
export const parseStatusParam = (searchParams) =>
  csvToArray(searchParams.get('status')).filter((v) => STATUS_VALUES.includes(v));

/**
 * Write the `status` URL param from a list of selected status ids. All-selected
 * or empty both clear the param — "no status param" already means "show all"
 * (see `parseStatusParam`'s default). Shared by GlobalFilterStrip's status
 * dropdown and MapPage's `setSelectedStatuses` (mobile "Map controls" panel)
 * so both write the exact same URL contract.
 */
export const setStatusParam = (searchParams, setSearchParams, statuses) => {
  const params = new URLSearchParams(searchParams);
  const isAll = Array.isArray(statuses) && statuses.length === STATUS_VALUES.length;
  const isEmpty = !Array.isArray(statuses) || statuses.length === 0;
  if (isAll || isEmpty) {
    params.delete('status');
  } else {
    params.set('status', statuses.join(','));
  }
  setSearchParams(params, { replace: true });
};

/**
 * Parse the canonical map URL params from a URLSearchParams instance.
 * Unknown / malformed values fall back to safe defaults (no silent filtering).
 */
export const parseMapParams = (searchParams) => ({
  commodity: searchParams.get('commodity') || '',
  layer: safe(searchParams.get('layer'), LAYER_MODES, 'all'),
  panel: safe(searchParams.get('panel'), PANEL_VALUES, null),
  status: parseStatusParam(searchParams),
  region: searchParams.get('region') || '',
  country: searchParams.get('country') || '',
  sidebarCollapsed: searchParams.get('sidebarCollapsed') === '1',
});

/**
 * Build a /map URL with the given params. Empty / default values are
 * omitted to keep URLs clean and shareable.
 */
export const buildMapLink = ({
  commodity = '',
  layer = '',
  panel = '',
  status = [],
  region = '',
  country = '',
  sidebarCollapsed = false,
} = {}) => {
  const p = new URLSearchParams();
  if (commodity) p.set('commodity', commodity);
  if (layer && layer !== 'all' && LAYER_MODES.includes(layer)) p.set('layer', layer);
  if (panel && PANEL_VALUES.includes(panel)) p.set('panel', panel);
  if (Array.isArray(status) && status.length > 0 && status.length < STATUS_VALUES.length) {
    p.set('status', status.join(','));
  }
  if (region) p.set('region', region);
  if (country) p.set('country', country);
  if (sidebarCollapsed) p.set('sidebarCollapsed', '1');
  const qs = p.toString();
  return qs ? `/map?${qs}` : '/map';
};
